import React, { useState, useMemo, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush } from 'recharts';
// Componente presentacional: recibe datos por props y construye la serie 07:00–22:00

// Componente de Tooltip personalizado
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const dp = payload[0]?.payload || null;
    if (!dp) return null;
    const realizadas = dp.realizadasOriginal ?? dp.realizadas ?? 0;
    const contestadas = dp.contestadasOriginal ?? dp.contestadas ?? 0;
    
    const tasa = ((contestadas / realizadas) * 100).toFixed(1);
    
    return (
      <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
        <p className="font-semibold text-gray-800 mb-2">{label}</p>
        <div className="space-y-1">
          <p className="text-sm">
            <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
            <span className="text-gray-600">Realizadas:</span>
            <span className="font-semibold ml-2">{realizadas}</span>
          </p>
          <p className="text-sm">
            <span className="inline-block w-3 h-3 rounded-full bg-orange-500 mr-2"></span>
            <span className="text-gray-600">Completadas:</span>
            <span className="font-semibold ml-2">{contestadas}</span>
          </p>
          <p className="text-sm text-gray-500 pt-1 border-t mt-2">
            Tasa: <span className="font-semibold">{tasa}%</span>
          </p>
        </div>
      </div>
    );
  }
  return null;
};

const EvolutionGraphic = ({ hourlyCalls = {}, hourlyAnswered = {}, totalCompleted = 0, onMetricsChange }) => {
  // Construir base 07–22 desde props
  const baseData = useMemo(() => {
    const rows = [];
    for (let h = 7; h <= 22; h++) {
      // Mostrar en HH:00 los datos de la franja anterior (HH-1:00 a HH:00)
      const sourceHour = h - 1;
      const key = String(sourceHour);
      const label = `${String(h).padStart(2, '0')}:00`;
      const realizadas = Number((hourlyCalls && hourlyCalls[key]) ?? 0) || 0;
      const contestadas = Number((hourlyAnswered && hourlyAnswered[key]) ?? 0) || 0;
      rows.push({ time: label, realizadas, contestadas });
    }
    return rows;
  }, [hourlyCalls, hourlyAnswered]);

  // Estado para controlar el rango seleccionado
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);

  useEffect(() => {
    const last = Math.max(0, baseData.length - 1);
    setStartIndex(0);
    setEndIndex(last);
  }, [baseData]);

  // Procesar datos para visualización con conectividad
  const processedData = useMemo(() => {
    return baseData.map((item) => {
      const isInactive = (item.realizadas || 0) === 0;
      return {
        ...item,
        realizadasDisplay: isInactive ? null : item.realizadas,
        contestadasDisplay: isInactive ? null : item.contestadas,
        realizadasOriginal: item.realizadas,
        contestadasOriginal: item.contestadas
      };
    });
  }, [baseData]);

  // Calcular métricas basadas en el rango seleccionado
  const metrics = useMemo(() => {
    const selectedData = processedData.slice(startIndex, endIndex + 1);
    
    // Filtrar solo las horas con llamadas realizadas (horas activas)
    const activeHours = selectedData.filter(item => item.realizadas > 0);
    
    const totales = selectedData.reduce((acc, curr) => ({
      realizadas: acc.realizadas + curr.realizadas,
      contestadas: acc.contestadas + curr.contestadas
    }), { realizadas: 0, contestadas: 0 });

    // Usar solo horas activas para calcular tasa de respuesta
    const tasaRespuesta = totales.realizadas > 0 
      ? ((totales.contestadas / totales.realizadas) * 100).toFixed(1)
      : 0;

    // Calcular promedios solo con horas activas
    const horasActivas = activeHours.length;
    const promedioRealizadas = horasActivas > 0 
      ? (totales.realizadas / horasActivas).toFixed(0)
      : 0;
    // Usar el valor de total_calls_completed de las tarjetas principales en lugar del cálculo por hora
    const promedioContestadas = horasActivas > 0 
      ? (Number(totalCompleted) / horasActivas).toFixed(0)
      : 0;

    const periodo = (selectedData[0] && selectedData[selectedData.length - 1])
      ? `${selectedData[0].time} - ${selectedData[selectedData.length - 1].time}`
      : '-';

    return {
      periodo,
      totalLlamadas: totales.realizadas,
      tasaRespuesta,
      promedioRealizadas,
      promedioContestadas,
      horasActivas
    };
  }, [startIndex, endIndex, processedData]);

  // Notificar métricas al componente padre cuando cambien
  useEffect(() => {
    if (typeof onMetricsChange === 'function') {
      onMetricsChange(metrics);
    }
  }, [metrics, onMetricsChange]);

  // Manejador del brush (selección de región)
  const handleBrushChange = (brushData) => {
    if (brushData && brushData.startIndex !== undefined && brushData.endIndex !== undefined) {
      setStartIndex(brushData.startIndex);
      setEndIndex(brushData.endIndex);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Gráfica principal */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <ResponsiveContainer width="100%" height={350}>
            <LineChart
              data={processedData}
              margin={{ top: 5, right: 42, left: -18, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="time"
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="line"
              />
              {/* Líneas principales con datos activos */}
              <Line
                type="monotone"
                dataKey="realizadasDisplay"
                stroke="#3b82f6"
                strokeWidth={3}
                dot={{ fill: '#3b82f6', r: 4 }}
                activeDot={{ r: 6 }}
                name="Llamadas Realizadas"
                isAnimationActive={false}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="contestadasDisplay"
                stroke="#ff7646"
                strokeWidth={3}
                dot={{ fill: '#ff7646', r: 4 }}
                activeDot={{ r: 6 }}
                name="Llamadas Completadas"
                isAnimationActive={false}
                connectNulls={false}
              />
              {/* Línea gris eliminada */}
              <Brush
                dataKey="time"
                height={30}
                stroke="#3b82f6"
                fill="#eff6ff"
                onChange={handleBrushChange}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Instrucciones */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">
            💡 Cómo usar:
          </h3>
          <ul className="text-sm text-blue-800 space-y-1 ml-4 list-disc">
            <li>Arrastra los extremos del selector azul para elegir un rango horario</li>
            <li>Las métricas superiores se actualizan automáticamente según tu selección</li>
            <li>Rango completo: 7:00 - 22:00 (líneas grises = periodos sin actividad)</li>
            <li>Las horas sin llamadas no se cuentan en los promedios ni tasa de respuesta</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EvolutionGraphic;